import { useEffect, useMemo, useState } from 'react'
import { usePageLoading } from '@/components/layout/PageLoadingProvider'
import {
  filterRecintosGeoJson,
  filterVotingPlaces,
  getRecintosGeoJson,
  getVotingPlaceLocationMatch,
  getVotingPlaceCatalogs,
} from '@/lib/data/recintos'
import type { InferredTerritory } from '@/lib/data/territoryLocation'
import type { VotingPlaceFilters as VotingPlaceFiltersState } from '@/types/votingPlace'
import { trackEvent } from '@/analytics'
import { VotingPlaceDetail } from './VotingPlaceDetail'
import { VotingPlaceFilters } from './VotingPlaceFilters'
import { VotingPlaceList } from './VotingPlaceList'
import { VotingPlaceMap, type VotingMapFocusRequest } from './VotingPlaceMap'

const MAX_VISIBLE_RECINTOS = 150

interface Props {
  detectedTerritory?: InferredTerritory | null
  locationStatusText?: string
  onFiltersChange?: (filters: VotingPlaceFiltersState) => void
}

function getFiltersKey(filters: VotingPlaceFiltersState) {
  return [
    filters.provincia ?? 'all',
    filters.canton ?? 'all',
    filters.parroquia ?? 'all',
    filters.zona ?? 'all',
  ].join(':')
}

export function VotingPlaceSection({
  detectedTerritory = null,
  locationStatusText,
  onFiltersChange,
}: Props) {
  const [filters, setFilters] = useState<VotingPlaceFiltersState>({})
  const [selectedRecintoId, setSelectedRecintoId] = useState<string | null>(null)
  const [mapFocusRequest, setMapFocusRequest] = useState<VotingMapFocusRequest | null>(null)
  const [geoJson, setGeoJson] = useState<GeoJSON.FeatureCollection | null>(null)
  const [loadingGeoJson, setLoadingGeoJson] = useState(true)
  usePageLoading(loadingGeoJson)

  useEffect(() => {
    let isMounted = true

    getRecintosGeoJson()
      .then((source) => {
        if (isMounted) setGeoJson(source)
      })
      .catch(() => {
        if (isMounted) setGeoJson(null)
      })
      .finally(() => { if (isMounted) setLoadingGeoJson(false) })

    return () => {
      isMounted = false
    }
  }, [])

  const catalogs = useMemo(() => getVotingPlaceCatalogs(filters), [filters])
  const filteredRecintos = useMemo(() => filterVotingPlaces(filters), [filters])

  const filteredIds = useMemo(
    () => new Set(filteredRecintos.map((recinto) => recinto.id)),
    [filteredRecintos],
  )

  const filteredGeoJson = useMemo(
    () => (geoJson ? filterRecintosGeoJson(geoJson, filteredIds) : null),
    [filteredIds, geoJson],
  )
  const locationMatch = useMemo(
    () => getVotingPlaceLocationMatch(detectedTerritory),
    [detectedTerritory],
  )

  const visibleRecintos = useMemo(
    () => filteredRecintos.slice(0, MAX_VISIBLE_RECINTOS),
    [filteredRecintos],
  )

  const selectedRecinto =
    filteredRecintos.find((recinto) => recinto.id === selectedRecintoId) ?? null

  useEffect(() => {
    if (selectedRecintoId && !filteredIds.has(selectedRecintoId)) {
      setSelectedRecintoId(null)
    }
  }, [filteredIds, selectedRecintoId])

  useEffect(() => {
    onFiltersChange?.(filters)
  }, [filters, onFiltersChange])

  useEffect(() => {
    if (!locationMatch) return

    setFilters(locationMatch.filters)
    setSelectedRecintoId(locationMatch.selectedRecintoId)
    setMapFocusRequest({
      type: 'bounds',
      key: `location:${getFiltersKey(locationMatch.filters)}`,
    })
    trackEvent('where_to_vote_location_apply', {
      event_category: 'where_to_vote',
      location_source: 'browser',
      location_matched: true,
    })
  }, [locationMatch])

  function handleFiltersChange(next: VotingPlaceFiltersState) {
    setFilters(next)
    setMapFocusRequest({
      type: 'bounds',
      key: `filters:${getFiltersKey(next)}:${Date.now()}`,
    })
    trackEvent('where_to_vote_filter', {
      event_category: 'where_to_vote',
      provincia: next.provincia,
      canton: next.canton,
      parroquia: next.parroquia,
      zona: next.zona,
    })
  }

  function handleClearFilters() {
    setFilters({})
    setMapFocusRequest({
      type: 'bounds',
      key: `filters:${getFiltersKey({})}:${Date.now()}`,
    })
    trackEvent('where_to_vote_filter_clear', {
      event_category: 'where_to_vote',
    })
  }

  function handleSelectRecinto(id: string, source: 'list' | 'map') {
    setSelectedRecintoId(id)
    setMapFocusRequest({
      type: 'recinto',
      recintoId: id,
      key: `recinto:${source}:${id}:${Date.now()}`,
    })
    trackEvent('voting_place_select', {
      event_category: 'where_to_vote',
      recinto_id: id,
      selection_source: source,
    })
  }

  return (
    <section className="voting-section" aria-labelledby="where-to-vote-title">
      <header className="voting-section__header">
        <h2 id="where-to-vote-title">Donde Votar</h2>
        {locationStatusText ? (
          <p className="voting-section__location">
            <strong>{locationMatch ? `📍 ${locationMatch.label}` : locationStatusText}</strong>
            {locationMatch ? <small>{locationStatusText}</small> : null}
          </p>
        ) : null}
      </header>
      <VotingPlaceFilters
        catalogs={catalogs}
        filters={filters}
        onChange={handleFiltersChange}
        onClear={handleClearFilters}
      />

      <div className="voting-layout">
        <VotingPlaceList
          recintos={visibleRecintos}
          totalCount={filteredRecintos.length}
          selectedRecintoId={selectedRecintoId}
          onSelectRecinto={(id) => handleSelectRecinto(id, 'list')}
        />

        <VotingPlaceMap
          focusRequest={mapFocusRequest}
          geojson={filteredGeoJson}
          selectedRecintoId={selectedRecintoId}
          onSelectRecinto={(id) => handleSelectRecinto(id, 'map')}
        />

        <VotingPlaceDetail recinto={selectedRecinto} />
      </div>
    </section>
  )
}
